import { useSettlementToken } from "features/tokens/useSettlementToken";
import React from "react";
import {
  REDEEM_STATUS,
  REDEEM_STATUS_CODE,
  REDEEM_STATUS_LABEL,
  RedeemStatus,
  type RedeemRequest,
} from "types/redeem";
import Button from "components/button/Button";
import formatDate from "utils/DateUtil";
import "./redeem-item.css";
import { useIssuerMerchants } from "pages/issuer-page/useIssuerMerchants";
import { useMerchantLookup } from "features/merchants/useMerchantLookup";

interface Props {
  request: RedeemRequest;
  onApprove: (
    requestId: string,
    merchantId: string,
    stripeAmount: number,
  ) => void;
  onReject: (requestId: string) => void;
  isProcessing: boolean;
}

const STATUS_BADGE: Record<RedeemStatus, string> = {
  0: `● ${REDEEM_STATUS_LABEL.PENDING}`,
  1: `● ${REDEEM_STATUS_LABEL.INREVIEW}`,
  2: `● ${REDEEM_STATUS_LABEL.INPROGRESS}`,
  3: `✓ ${REDEEM_STATUS_LABEL.APPROVED}`,
  4: `✕ ${REDEEM_STATUS_LABEL.REJECTED}`,
};

const activeRedeemStatuses = [
  REDEEM_STATUS_CODE.PENDING,
  REDEEM_STATUS_CODE.INREVIEW,
];
function RedeemItem({ request, onApprove, onReject, isProcessing }: Props) {
  const { label: selectedTokenLabel } = useSettlementToken();
  const {
    id,
    merchantId,
    amount,
    status,
    createdAt,
    settlementTokenSymbol,
  } = request;
  const { merchantsMap, loadingMerchants } = useIssuerMerchants();
  const knownName = merchantsMap[merchantId];

  // The map only holds the recent merchants, so an older merchant's redeem misses it —
  // fall back to the on-chain lookup instead of rendering the row with no name.
  const lookup = useMerchantLookup(merchantId, !knownName && !loadingMerchants);
  const resolvedName = knownName || lookup.merchant?.merchantName;
  const pendingName =
    !resolvedName && (loadingMerchants || lookup.status === "loading");
  // Neither source has a name: show the merchant id rather than nothing.
  const label = resolvedName || merchantId;

  const statusClass = REDEEM_STATUS[status].toLowerCase();
  return (
    <div className="rdm-item">
      <div className="rdm-item__info">
        <span className="rdm-item__merchant">
          {id}{" "}
          {pendingName ? (
            <span
              className="rdm-skeleton-bar rdm-skeleton-bar--merchant"
              aria-hidden="true"
            />
          ) : (
            <span>{label}</span>
          )}
        </span>
        <span className="rdm-item__date">
          {formatDate(new Date(createdAt))}
        </span>
      </div>

      <div className="rdm-item__amount">
        {amount.toFixed(2)}{" "}
        <span className="rdm-item__unit">
          {settlementTokenSymbol ?? selectedTokenLabel}
        </span>
      </div>

      <div className="rdm-item__right">
        {activeRedeemStatuses.includes(status) ? (
          <div className="rdm-item__actions">
            <Button
              variant="success"
              size="sm"
              disabled={isProcessing}
              onClick={() => onApprove(id, merchantId, amount)}
            >
              Approve
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={isProcessing}
              onClick={() => onReject(id)}
            >
              Reject
            </Button>
          </div>
        ) : (
          <span
            className={`rdm-item__badge rdm-item__badge--${statusClass.split(" ").join("-")}`}
          >
            {STATUS_BADGE[status]}
          </span>
        )}
      </div>
    </div>
  );
}

export default RedeemItem;
